import { computed, type MaybeRefOrGetter, toValue } from 'vue'

import {
  AdmissionModeCh,
  AdmissionModeFr,
  ReleaseModeCh,
  ReleaseModeFr,
} from '../types/admission-release-mode'
import { CountryEnum } from '../types/country'
import {
  DestinationCh,
  DestinationFr,
  OriginCh,
  OriginFr,
} from '../types/origin-destination'

interface AdmissionReleaseSource {
  admissionMode: string | null
  releaseMode: string | null
  origin: string | null
  destination: string | null
}

// Looks up a code in a label map. CH codes can be padded ('01'), FR codes are
// single chars but may be letters ('R', 'N'). Try the numeric form first
// (so '01' resolves), then the raw string, then fall back to the code itself.
function lookup(map: { [key: string]: string }, code: string): string {
  const numeric = Number(code)
  if (!Number.isNaN(numeric) && map[numeric] !== undefined) {
    return map[numeric]
  }
  return map[code] ?? code
}

type Translator = (key: string) => string

export function useAdmissionReleaseInfo(
  source: MaybeRefOrGetter<AdmissionReleaseSource>,
  country: MaybeRefOrGetter<CountryEnum> = CountryEnum.FR,
  t?: Translator,
) {
  const admissionMode = computed(() => toValue(source).admissionMode || '-')
  const releaseMode = computed(() => toValue(source).releaseMode || '-')
  const origin = computed(() => toValue(source).origin || '-')
  const destination = computed(() => toValue(source).destination || '-')

  const admissionLabel = computed(() => toValue(country) === CountryEnum.CH
    ? lookup(AdmissionModeCh, admissionMode.value)
    : lookup(AdmissionModeFr, admissionMode.value))

  const releaseLabel = computed(() => toValue(country) === CountryEnum.CH
    ? lookup(ReleaseModeCh, releaseMode.value)
    : lookup(ReleaseModeFr, releaseMode.value))

  const originLabel = computed(() => toValue(country) === CountryEnum.CH
    ? lookup(OriginCh as { [key: string]: string }, origin.value)
    : lookup(OriginFr, origin.value))

  const destinationLabel = computed(() => toValue(country) === CountryEnum.CH
    ? lookup(DestinationCh as { [key: string]: string }, destination.value)
    : lookup(DestinationFr, destination.value))

  const admissionTooltip = computed(() => t
    ? `${t('stayDisplayer.generalStayData.admissionMode')} : ${admissionLabel.value}<br />${t('stayDisplayer.generalStayData.originLabel')} : ${originLabel.value}`
    : '')
  const releaseTooltip = computed(() => t
    ? `${t('stayDisplayer.generalStayData.releaseMode')} : ${releaseLabel.value}<br />${t('stayDisplayer.generalStayData.destinationLabel')} : ${destinationLabel.value}`
    : '')

  return {
    admissionMode,
    releaseMode,
    origin,
    destination,
    admissionLabel,
    releaseLabel,
    originLabel,
    destinationLabel,
    admissionTooltip,
    releaseTooltip,
  }
}
